import React, { useState, useEffect } from 'react';
import { deliveriesAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Eye, Package, CheckCircle, XCircle, Camera, PencilSimpleLine } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const statusColors = {
  pending: 'status-pending',
  arrived: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400',
  in_progress: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  completed: 'status-success',
  failed: 'status-error',
  partial: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400'
};

const DeliveriesPage = () => {
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  useEffect(() => {
    fetchDeliveries();
  }, [statusFilter]);

  const fetchDeliveries = async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const response = await deliveriesAPI.list(params);
      setDeliveries(response.data);
    } catch (error) {
      toast.error('Failed to load deliveries');
    } finally {
      setLoading(false);
    }
  };

  const viewDelivery = async (id) => {
    try {
      const response = await deliveriesAPI.get(id);
      setSelectedDelivery(response.data);
      setIsDetailOpen(true);
    } catch (error) {
      toast.error('Failed to load delivery details');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="deliveries-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold">Deliveries</h1>
          <p className="text-muted-foreground">View delivery history and ePOD details</p>
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full" />
            </div>
          ) : deliveries.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              No deliveries found
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Delivery #</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Site</TableHead>
                  <TableHead>Driver</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>ePOD</TableHead>
                  <TableHead>Completed</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {deliveries.map((delivery) => (
                  <TableRow key={delivery.id} data-testid={`delivery-row-${delivery.id}`}>
                    <TableCell className="font-mono text-sm">{delivery.delivery_number}</TableCell>
                    <TableCell>{delivery.customer_name || 'N/A'}</TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                      {delivery.site_name || 'N/A'}
                    </TableCell>
                    <TableCell>{delivery.driver_name || 'N/A'}</TableCell>
                    <TableCell>
                      <Badge className={statusColors[delivery.status] || 'bg-slate-100'}>
                        {delivery.status?.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {delivery.signature ? (
                          <PencilSimpleLine className="w-4 h-4 text-emerald-500" title="Signature captured" />
                        ) : (
                          <PencilSimpleLine className="w-4 h-4 text-muted-foreground/30" />
                        )}
                        {delivery.photos?.length > 0 ? (
                          <Camera className="w-4 h-4 text-emerald-500" title={`${delivery.photos.length} photos`} />
                        ) : (
                          <Camera className="w-4 h-4 text-muted-foreground/30" />
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {delivery.completed_at 
                        ? format(new Date(delivery.completed_at), 'dd/MM/yy HH:mm')
                        : '-'
                      }
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => viewDelivery(delivery.id)}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Delivery Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              Delivery {selectedDelivery?.delivery_number}
            </DialogTitle>
          </DialogHeader>
          {selectedDelivery && (
            <div className="space-y-6 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Customer</p>
                  <p className="font-medium">{selectedDelivery.customer_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Site</p>
                  <p className="font-medium">{selectedDelivery.site_name}</p>
                  <p className="text-sm text-muted-foreground">{selectedDelivery.site_address}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Driver</p>
                  <p className="font-medium">{selectedDelivery.driver_name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <Badge className={statusColors[selectedDelivery.status]}>
                    {selectedDelivery.status?.replace('_', ' ')}
                  </Badge>
                </div>
              </div>

              {/* Delivery Lines */}
              {selectedDelivery.lines?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Delivered Products</h4>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Product</TableHead>
                          <TableHead className="text-right">Ordered</TableHead>
                          <TableHead className="text-right">Delivered</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedDelivery.lines.map((line) => (
                          <TableRow key={line.id}>
                            <TableCell>{line.product_name}</TableCell>
                            <TableCell className="text-right font-mono">
                              {line.ordered_quantity?.toLocaleString()} L
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {line.delivered_quantity?.toLocaleString()} L
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Signature */}
              {selectedDelivery.signature && (
                <div>
                  <h4 className="font-medium mb-2">Signature</h4>
                  <div className="p-4 bg-muted rounded-lg">
                    <p className="text-sm text-muted-foreground mb-2">
                      Signed by: {selectedDelivery.signature.signer_name}
                    </p>
                    {selectedDelivery.signature.data_url && (
                      <img
                        src={selectedDelivery.signature.data_url}
                        alt="Signature"
                        className="max-h-24 border rounded"
                      />
                    )}
                  </div>
                </div>
              )}

              {/* Photos */}
              {selectedDelivery.photos?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Photos ({selectedDelivery.photos.length})</h4>
                  <div className="grid grid-cols-3 gap-2">
                    {selectedDelivery.photos.map((photo) => (
                      <div key={photo.id} className="aspect-square bg-muted rounded-lg overflow-hidden">
                        <img src={photo.url} alt={photo.description || 'Delivery photo'} className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes */}
              {selectedDelivery.notes && (
                <div>
                  <h4 className="font-medium mb-2">Notes</h4>
                  <p className="text-sm p-3 bg-muted rounded-lg">{selectedDelivery.notes}</p>
                </div>
              )}

              {/* Timestamps */}
              <div className="grid grid-cols-2 gap-4 text-sm pt-4 border-t">
                {selectedDelivery.arrived_at && (
                  <div>
                    <p className="text-muted-foreground">Arrived</p>
                    <p>{format(new Date(selectedDelivery.arrived_at), 'dd/MM/yyyy HH:mm')}</p>
                  </div>
                )}
                {selectedDelivery.completed_at && (
                  <div>
                    <p className="text-muted-foreground">Completed</p>
                    <p>{format(new Date(selectedDelivery.completed_at), 'dd/MM/yyyy HH:mm')}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DeliveriesPage;
